import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '@orderna/admin-panel/src/environments/environment';
import { InventoryItem } from '../../model/inventory';
import { IInventoryService } from './inventory-api.interface';
import { SuccessResponse } from '../shared/success-response';
import { FindInventoriesQueryDto } from './dtos/queries/find-inventories.query.dto';
import { FindInventoriesResponseDto } from './dtos/queries/find-inventories.response.dto';
import { FindInventoryNamesQueryDto } from './dtos/queries/find-inventory-names.query.dto';
import { FindInventoryNamesResponseDto } from './dtos/queries/find-inventory-names.response.dto';
import { FindInventoryResponseDto } from './dtos/queries/find-inventory.response.dto';
import { InventoryApiMapper } from './inventory-api.mapper';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';
import { ExportInventoriesQueryDto } from './dtos/queries/export-inventories.query.dto';
import { CreateInventoryResponseDto } from './dtos/commands/create-inventory.response.dto';
import { CreateInventoryRequestDto } from './dtos/commands/create-inventory.request.dto';
import { UpdateInventoryRequestDto } from './dtos/commands/update-inventory.request.dto';
import { UpdateInventoryResponseDto } from './dtos/commands/update-inventory.response.dto';
import { UpdateInventorySettingsRequestDto } from './dtos/commands/update-inventory-settings.request.dto';
import { UpdateInventorySettingsResponseDto } from './dtos/commands/update-inventory-settings.response.dto';
import { FindInventoryDetailsResponseDto } from './dtos/queries/find-inventory-details.response.dto';
import { FindInventoryNamesByStoresResponseDto } from './dtos/queries/find-inventory-names-by-stores.response.dto';
import { FindInventoryNamesByStoresQueryDto } from './dtos/queries/find-inventory-names-by-stores.query.dto';
import { FindInventoryQuickRestockDetailsResponseDto } from './dtos/queries/find-inventory-quick-restock-details.response.dto';

@Injectable({
  providedIn: 'root',
})
export class InventoryApiService implements IInventoryService {
  private readonly API_URL = `${environment.api}/inventories`;

  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);

  async createInventory(
    data: CreateInventoryRequestDto,
  ): Promise<CreateInventoryResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateInventoryResponseDto>>(
        `${this.API_URL}`,
        data,
      ),
    );

    return response.data;
  }

  async updateInventory(
    inventoryId: number,
    data: UpdateInventoryRequestDto,
  ): Promise<UpdateInventoryResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateInventoryResponseDto>>(
        `${this.API_URL}/${inventoryId}`,
        data,
      ),
    );

    return response.data;
  }

  async updateInventorySettings(
    inventoryId: number,
    data: UpdateInventorySettingsRequestDto,
  ): Promise<UpdateInventorySettingsResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateInventorySettingsResponseDto>>(
        `${this.API_URL}/${inventoryId}/settings`,
        data,
      ),
    );

    return response.data;
  }

  async findInventories(
    query: FindInventoriesQueryDto,
  ): Promise<FindInventoriesResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindInventoriesResponseDto>>(
        `${this.API_URL}`,
        {
          params,
        },
      ),
    );

    return response.data;
  }

  async exportInventoryItems(
    query: ExportInventoriesQueryDto,
    options?: HandleBlobHttpResponseOptions,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${this.API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      }),
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async findInventoryNames(
    query: FindInventoryNamesQueryDto,
  ): Promise<FindInventoryNamesResponseDto> {
    const params = new HttpParams({
      fromObject: query as any,
    });

    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindInventoryNamesResponseDto>>(
        `${this.API_URL}/names`,
        { params },
      ),
    );

    return response.data;
  }

  async findInventoryNamesByStores(
    query: FindInventoryNamesByStoresQueryDto,
  ): Promise<FindInventoryNamesByStoresResponseDto> {
    const params = new HttpParams({
      fromObject: query as any,
    });

    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindInventoryNamesByStoresResponseDto>>(
        `${this.API_URL}/names/by-stores`,
        { params },
      ),
    );

    return response.data;
  }

  async findInventory(
    inventoryId: InventoryItem['id'],
  ): Promise<FindInventoryResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindInventoryResponseDto>>(
        `${this.API_URL}/${inventoryId}/edit`,
      ),
    );

    return response.data;
  }

  async findInventoryDetails(
    inventoryId: InventoryItem['id'],
  ): Promise<FindInventoryDetailsResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindInventoryDetailsResponseDto>>(
        `${this.API_URL}/${inventoryId}/details`,
      ),
    );

    return response.data;
  }

  async findInventoryQuickRestockDetails(
    inventoryId: InventoryItem['id'],
  ): Promise<FindInventoryQuickRestockDetailsResponseDto> {
    const response = await firstValueFrom(
      this.http.get<
        SuccessResponse<FindInventoryQuickRestockDetailsResponseDto>
      >(`${this.API_URL}/${inventoryId}/quick-restock-details`),
    );

    return response.data;
  }

  async deleteInventory(
    inventoryId: InventoryItem['id'],
  ): Promise<{ deletedCount: number; deletedIds: InventoryItem['id'][] }> {
    const response = await firstValueFrom(
      this.http.delete<SuccessResponse>(`${this.API_URL}/${inventoryId}`),
    );

    return InventoryApiMapper.toDeleteDetails(response.data);
  }

  async deleteInventories(
    inventoryIds: InventoryItem['id'][],
  ): Promise<{ deletedCount: number; deletedIds: InventoryItem['id'][] }> {
    const response = await firstValueFrom(
      this.http.delete<SuccessResponse>(`${this.API_URL}/delete-many`, {
        body: { ids: inventoryIds },
      }),
    );

    return InventoryApiMapper.toDeleteDetails(response.data);
  }
}
